import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  WritableSignal,
  inject,
  output,
  signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import { finalize } from 'rxjs';
import { GstarClient } from 'src/app/web-api-client';
import { IncomeType } from './income-type';

@Component({
  standalone: false,
  selector: 'gstar-income-types-combobox',
  templateUrl: './income-types-combobox.component.html',
  styleUrls: ['./income-types-combobox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncomeTypesComboboxComponent implements OnInit {
  private readonly gstarClient = inject(GstarClient);
  private readonly translateService = inject(TranslateService);

  public currentSecurityTypeId: number | null = null;

  @Input() value!: IncomeType | null | undefined;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() placeholder: string = this.translateService.instant(
    'admin.generalRatesRuleEngine.incomeType'
  );

  @Input() set securityTypeId(value: number | null | undefined) {
    this.currentSecurityTypeId = value ?? null;
    this.loadIncomeTypes();
  }

  selectIncomeType = output<IncomeType | null | undefined>();
  clearIncomeType = output<void>();

  incomeTypes: WritableSignal<IncomeType[]> = signal<IncomeType[]>([]);
  listLoading = signal<boolean>(true);

  ngOnInit(): void {
    this.loadIncomeTypes();
  }

  selectIncomeTypeHandler(incomeType: IncomeType | null | undefined): void {
    this.value = incomeType;
    this.selectIncomeType.emit(incomeType);
  }

  clearIncomeTypeHandler(): void {
    this.value = null;
    this.clearIncomeType.emit();
  }

  private loadIncomeTypes(): void {
    if (!this.currentSecurityTypeId) {
      this.incomeTypes.set([]);
      this.listLoading.set(false);
      return;
    }

    this.listLoading.set(true);

    this.gstarClient
      .taxresultitemtypesGET(
        undefined,
        `securityTypeId eq ${this.currentSecurityTypeId}`,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(finalize(() => this.listLoading.set(false)))
      .subscribe(response => {
        const source = response.value?.[0]?.taxResultItemTypes ?? [];

        const mapped = source
          .filter(item => !!item.taxResultItemTypeId)
          .map(item => ({
            taxResultItemTypeId: item.taxResultItemTypeId ?? 0,
            name: item.name ?? '',
          }));

        this.incomeTypes.set(cloneDeep(mapped));

        const selectedId = this.value?.taxResultItemTypeId;
        if (
          selectedId &&
          mapped.every(item => item.taxResultItemTypeId !== selectedId)
        ) {
          this.clearIncomeTypeHandler();
        }
      });
  }
}
